
import { useState } from "react";
import { Edit2, Trash2, Eye, BarChart2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { deleteServiceProvider } from "@/services/providers/providerOwnerService";
import { Link } from "react-router-dom";
import { useAreaDisplay } from "@/hooks/useAreaDisplay";

interface SubscriptionDataLite {
  subscribed?: boolean;
  tier?: string;
}

interface UserServiceCardProps {
  service: {
    id: string;
    name: string;
    description: string;
    category: string;
    categories?: string[];
    location?: string;
    suburbs?: string[];
    status: string;
    created_at: string;
    images?: string[];
  };
  subscriptionData?: SubscriptionDataLite | null;
  onDelete: () => void;
}

const UserServiceCard = ({ service, subscriptionData, onDelete }: UserServiceCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const { areaSummaryLabel } = useAreaDisplay(service.suburbs || []);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteServiceProvider(service.id!);
      
      toast({
        title: "Service deleted",
        description: "Your service listing has been deleted successfully.",
      });
      
      onDelete(); // Call the onDelete callback to refresh the list
    } catch (error) {
      console.error("Error deleting service:", error);
      toast({
        title: "Error",
        description: "Failed to delete the service. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "border-teal bg-teal-soft text-teal";
      case "pending":
        return "border-amber bg-amber-soft text-brand-ink";
      case "rejected":
        return "border-red-100 bg-red-50 text-coral";
      default:
        return "border-brand-cloud bg-brand-mist text-brand-slate";
    }
  };

  // For demonstration purposes - use the actual image from service or a placeholder
  const serviceImage = service.images && service.images[0] ? service.images[0] : "https://placehold.co/400x200";
  const categoryLabel = service.categories && service.categories.length > 0
    ? service.categories.join(', ')
    : service.category;
  const locationLabel = service.suburbs && service.suburbs.length > 0
    ? areaSummaryLabel
    : (service.location || 'No location specified');

  // Check subscription status for analytics access
  const isFreeTier = !subscriptionData?.subscribed || subscriptionData?.tier === 'free_tier';
  const canViewAnalytics = !isFreeTier;
  const canViewPublic = service.status === 'approved';

  return (
    <>
      <div className="user-listing-card flex h-full flex-col overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm">
        {/* Image with approved badge */}
        <div className="relative">
          <img
            src={serviceImage}
            alt={service.name}
            className="w-full h-48 object-cover"
            loading="lazy"
            decoding="async"
          />
          {service.status === "approved" && (
            <div className="absolute top-3 right-3">
              <div className="flex items-center rounded-md bg-white/90 px-3 py-1 text-sm font-medium text-teal">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.75 12L10.58 14.83L16.25 9.17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="ml-1">Approved</span>
              </div>
            </div>
          )}
          {service.status === "pending" && (
            <div className="absolute top-3 right-3">
              <div className="flex items-center rounded-md bg-white/90 px-3 py-1 text-sm font-medium text-brand-ink">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="ml-1">Pending</span>
              </div>
            </div>
          )}
          {service.status === "rejected" && (
            <div className="absolute top-3 right-3">
              <div className="flex items-center rounded-md bg-white/90 px-3 py-1 text-sm font-medium text-coral">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="ml-1">Rejected</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Service information - flex-grow to push actions to bottom */}
        <div className="p-5 flex-grow flex flex-col">
          <h3 className="public-card-title">{service.name}</h3>

          <p className="mt-3 mb-2 line-clamp-2 public-small">{service.description}</p>
          <div className="space-y-1 text-xs text-brand-slate">
            <p>
              <span className="font-medium">Categories:</span> {categoryLabel}
            </p>
            <p>
              <span className="font-medium">Areas:</span> {locationLabel}
            </p>
          </div>
        </div>
        
        {/* Action buttons - always at bottom */}
        <div className="mt-auto flex divide-x divide-brand-cloud border-t border-brand-cloud">
          {canViewPublic && (
            <Button 
              variant="ghost" 
              className="flex h-auto flex-1 items-center justify-center rounded-none px-2 py-3 text-brand-slate hover:bg-brand-mist hover:text-brand-ink"
              asChild
            >
              <Link to={`/service/${service.id}`}>
                <Eye className="user-listing-action-icon h-4 w-4" />
                <span className="user-listing-action-label">View</span>
              </Link>
            </Button>
          )}
          {canViewAnalytics && (
            <Button 
              variant="ghost" 
              className="flex h-auto flex-1 items-center justify-center rounded-none px-2 py-3 text-brand-slate hover:bg-brand-mist hover:text-brand-ink"
              asChild
            >
              <Link to={`/analytics/${service.id}`}>
                <BarChart2 className="user-listing-action-icon h-4 w-4" />
                <span className="user-listing-action-label">Analytics</span>
              </Link>
            </Button>
          )}
          <Button 
            variant="ghost" 
            className="flex h-auto flex-1 items-center justify-center rounded-none px-2 py-3 text-brand-slate hover:bg-brand-mist hover:text-brand-ink"
            asChild
          >
            <Link to={`/submit-listing?edit=${service.id}`}>
              <Edit2 className="user-listing-action-icon h-4 w-4" />
              <span className="user-listing-action-label">Edit</span>
            </Link>
          </Button>
          <Button 
            variant="ghost" 
            className="flex h-auto flex-1 items-center justify-center rounded-none px-2 py-3 text-coral hover:bg-red-50 hover:text-coral"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="user-listing-action-icon h-4 w-4" />
            <span className="user-listing-action-label">Delete</span>
          </Button>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your service listing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-coral hover:bg-coral/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default UserServiceCard;
